import { Model } from '@/services/api/lib'
const qs = require('qs')

class User extends Model {
  resource () {
    return 'auth/v1/users'
  }

  constructor (...attributes) {
    if (attributes[0] !== undefined) {
      let obj = attributes[0]

      if (obj.permissions !== undefined) {
        obj.permissions.forEach((item) => {
          item.resourceProp = item.resource
          delete item.resource
        })
      }
      super(obj)
      return this
    }

    super(...attributes)
  }

  async save () {
    if (this.type === 'admin') {
      this.without(['store', 'organisation', 'generate_password', 'require_password_change'])

      if (this.password === '') {
        this.without(['password', 'password_confirmation'])
      }
    } else {
      this.beforeSave()
    }

    const isValid = await this.validate()

    if (!isValid) {
      throw new Error('Validation failed')
    }

    let config = {
      method: this.hasId() ? 'PUT' : 'POST',
      url: this.endpoint(),
      data: this.payload()
    }

    if (this.type === 'admin') {
      config.headers = {
        'x-user-type': 'admin'
      }
    }

    const { data } = await this.request(config)

    if (data.permissions !== undefined) {
      data.permissions.forEach((item) => {
        item.resourceProp = item.resource
        delete item.resource
      })
    }

    this._attributes = Object.assign(this._attributes, data)
    this.setup()
    return this
  }

  beforeSave () {
    if (this.generate_password) {
      this.without(['password', 'password_confirmation'])
      return
    }

    if (this.password === '') {
      this.without(['password', 'password_confirmation', 'generate_password'])

      if (!this.require_password_change) {
        this.without(['require_password_change'])
      }
      return
    }

    this.without(['generate_password'])
  }

  login (credentials) {
    return this.request({
      method: 'POST',
      url: '/auth/v1/oauth/token',
      data: qs.stringify(credentials)
    }).then(response => response.data)
  }

  defaults () {
    return {
      email: '',
      name: '',
      type: '',
      password: '',
      password_confirmation: '',
      role_ids: [],
      permission_ids: [],
      generate_password: false,
      require_password_change: false,
      accepted_tos: null
    }
  }

  async getMe () {
    const { data } = await this.request({
      url: `${this.resource()}/me`,
      method: 'GET',
      headers: {
        'x-user-type': 'admin'
      }
    })

    return new this.constructor(data)
  }

  async getByCustomerId (id) {
    const url = `${this.resource()}?filter[customer_id]=${id}`

    const { data } = await this.request({
      url,
      method: 'GET'
    })

    let object = Array.isArray(data) ? data[0] : data
    return new this.constructor(object)
  }

  async resetPassword (data) {
    const url = `${this.resource()}/password/reset`

    return this.request({
      url,
      method: 'POST',
      data: data
    })
  }

  async generateNewPassword () {
    const url = `${this.resource()}/${this.id}/password/generate`

    return this.request({
      url,
      method: 'PATCH'
    })
  }
}

class Role extends Model {
  resource () {
    return 'auth/v1/roles'
  }

  primaryKey () {
    return 'id'
  }

  defaults () {
    return {
      name: '',
      permission_ids: [],
      user_ids: []
    }
  }

  validations () {
    return {
      name: 'required|string',
      code: 'string',
      permission_ids: 'array',
      user_ids: 'array'
    }
  }

  constructor (...attributes) {
    if (attributes[0] !== undefined) {
      let obj = attributes[0]

      if (obj.permissions !== undefined) {
        obj.permissions.forEach((item) => {
          item.resourceProp = item.resource
          delete item.resource
        })
      }
      super(obj)
      return this
    }

    super(...attributes)
  }

  async save () {
    const isValid = await this.validate()

    if (!isValid) {
      throw new Error('Validation failed')
    }

    const { data } = this.id ? await this._update() : await this._create()

    if (data.permissions !== undefined) {
      data.permissions.forEach((item) => {
        item.resourceProp = item.resource
        delete item.resource
      })
    }

    this._attributes = Object.assign(this._attributes, data)
    this.setup()
    return this
  }

  _create () {
    return this.request({
      method: 'POST',
      url: 'auth/v1/roles',
      data: this.payload()
    })
  }
}

class Permission extends Model {
  resource () {
    return 'auth/v1/permissions'
  }

  primaryKey () {
    return 'id'
  }

  defaults () {
    return {
      name: '',
      app: '',
      store: '',
      action: '',
      resourceProp: ''
    }
  }

  constructor (...attributes) {
    if (attributes[0] !== undefined) {
      let obj = attributes[0]
      obj.resourceProp = obj.resource
      delete obj.resource
      super(obj)
      return this
    }

    super(...attributes)
  }

  async save () {
    const isValid = await this.validate()

    if (!isValid) {
      throw new Error('Validation failed')
    }

    const { data } = this.hasId() ? await this._update() : await this._create()
    data.resourceProp = data.resource
    delete data.resource
    this._attributes = Object.assign(this._attributes, data)
    this.setup()
    return this
  }

  payload () {
    let payload = Object.keys(this._attributes)
      .filter(x => !this._without.includes(x))
      .reduce((obj, key) => {
        obj[key] = this._attributes[key]
        return obj
      }, {})

    // replace placeholder with real attribute
    payload.resource = payload.resourceProp
    delete payload.resourceProp

    return payload
  }
}

class Client extends Model {
  resource () {
    return 'auth/v1/clients'
  }
}

class ToS extends Model {
  resource () {
    return 'auth/v1/terms'
  }

  async getAdminToS () {
    const result = await this.orderBy('-version')
      .where('type', 'eq', 'admin')
      .limit(1)
      .get()

    return result.shift()
  }
}

export default {
  User,
  Client,
  Role,
  Permission,
  ToS
}
